// libs
import React, {
  memo,
  useRef,
  useEffect,
  useState,
  useReducer,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { Swipeable } from 'react-swipeable';
import isEmpty from 'lodash/isEmpty';

// components
import NavButton from './components/NavButton';

// contexts
import ResponsiveContext from 'shared/components/ResponsiveLoader/ResponsiveContext';

// helpers
import { carouselReducer } from './helpers';

// constants
import { CAROUSEL_TYPE, NAV_BUTTON, ACTIONS } from './constants';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { TAB_INDEX } from 'shared/constants';

// styles
import { Container, Button, Reel } from './styles';

//analytics
import NFA from 'shared/helpers/analytics/NFAnalytics';

interface ICarouselProps {
  children: React.ReactNodeArray;
  slidesToMove?: number;
  slidesToShow?: number;
  currentSlide?: number;
  type?: CAROUSEL_TYPE;
  slideWidth: number;
  slideHeight: number;
  navPadding?: number;
  showNav?: boolean;
  navCustomStyles?: React.CSSProperties;
  onChange?: (currentPage: number) => void;
  nextButton?: React.ReactNode;
  prevButton?: React.ReactNode;
  autoPlay?: boolean;
  autoPlayDuration?: number;
  loopOverSlides?: boolean;
  ignoreRightMargin?: boolean;
  customRightMargin?: number;
  isBottomNav?: boolean;
  className?: string;
  slideClassName?: string;
  overrideSlideStyles?: React.CSSProperties;
  sendFirstImageSwipeTracking?: VoidFunction;
}

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

function Carousel({
  children,
  slidesToShow = 1,
  slidesToMove = 1,
  currentSlide = 0,
  navPadding = 30,
  type = CAROUSEL_TYPE.HORIZONTAL,
  slideWidth,
  slideHeight,
  showNav = true,
  onChange = EMPTY_FUNC,
  nextButton,
  prevButton,
  autoPlay = false,
  autoPlayDuration = 5000,
  navCustomStyles = {},
  loopOverSlides = false,
  ignoreRightMargin = false,
  customRightMargin = 20,
  isBottomNav = false,
  className = '',
  slideClassName = '',
  overrideSlideStyles = {},
  sendFirstImageSwipeTracking,
}: ICarouselProps) {
  const [reelStyles, setReelStyles] = useState<React.CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const reelRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<HTMLDivElement[]>([]);
  const { deviceWidth } = useContext(ResponsiveContext);
  const [isCarouselFirstScroll, setIsCarouselFirstScroll] = useState(true);
  const numberOfImagesSwiped = useRef(new Set([]));

  const initialCarouselState = {
    slidesToMove,
    slideWidth,
    slideHeight,
    type,
    showNav,
    navPadding,
    canShowNav: showNav,
    numberOfSlides: children.length,
    containerWidth: 0,
    containerHeight: 0,
    slidesInFrame: slidesToShow,
    realSlideIndex: currentSlide,
    slideIndex: currentSlide,
    slideMargin: 0,
    posX: 0,
    posY: 0,
  };

  if (children.length < slidesToShow) {
    initialCarouselState.slidesInFrame = initialCarouselState.numberOfSlides;
  }

  const [carouselState, carouselDispatch] = useReducer(carouselReducer, initialCarouselState);

  // Update Slide Refs on children change
  useEffect(() => {
    if (slidesRef.current.length !== children.length) {
      slidesRef.current = slidesRef.current.slice(0, children.length);
    }
    carouselDispatch({
      type: ACTIONS.SET_NUMBER_SLIDES,
      payload: { numberOfSlides: children.length },
    });
  }, [children.length]);

  useEffect(() => {
    carouselDispatch({ type: ACTIONS.SET_SLIDE, payload: { slideIndex: currentSlide } });
  }, [currentSlide]);

  useEffect(() => {
    onChange(carouselState.realSlideIndex);
  }, [carouselState.realSlideIndex, onChange]);

  const updateImagesSwipedCount = useCallback(() => {
    numberOfImagesSwiped.current = numberOfImagesSwiped.current.add(carouselState.realSlideIndex);
    NFA.updateDatalayerBeforeNext(
      {
        pdpCarouselImageScrolled: numberOfImagesSwiped.current.size,
        pdpCarouselImagesCount: children.length,
      },
      TRACK_TYPE.PAGELOAD
    );
  }, [carouselState.realSlideIndex, children.length]);

  // Set Carousal Dimensions
  useEffect(() => {
    if (containerRef.current !== null) {
      const { width: elWidth, height: elHeight } = containerRef.current.getBoundingClientRect();

      if (elWidth !== carouselState.containerWidth || elHeight !== carouselState.containerHeight) {
        carouselDispatch({
          type: ACTIONS.SET_DIMENSIONS,
          payload: {
            slidesToMove,
            slideWidth,
            slideHeight,
            slidesInFrame: slidesToShow,
            containerWidth: elWidth,
            containerHeight: elHeight,
          },
        });
      }
    }
  }, [
    deviceWidth,
    carouselState.containerWidth,
    carouselState.containerHeight,
    carouselState.numberOfSlides,
    slidesToShow,
    slidesToMove,
    slideWidth,
    slideHeight,
  ]);

  // On Slide Index change set Carousel FRAME Position
  useEffect(() => {
    if (reelRef.current === null || isEmpty(slidesRef.current)) {
      return;
    }

    const slideRef = slidesRef.current[carouselState.slideIndex];

    if (!slideRef) {
      return;
    }

    if (carouselState.type === CAROUSEL_TYPE.HORIZONTAL) {
      let translateX =
        -1 * reelRef.current.getBoundingClientRect().left + slideRef.getBoundingClientRect().left;

      const maxTranslateX =
        -1 * reelRef.current.getBoundingClientRect().right +
        slidesRef.current[slidesRef.current.length - 1].getBoundingClientRect().right;

      if (translateX > maxTranslateX) {
        translateX = maxTranslateX;
      }

      if (translateX !== carouselState.posX) {
        carouselDispatch({
          type: ACTIONS.SET_POS,
          payload: {
            posX: translateX,
            posY: 0,
          },
        });
      }
    } else {
      const translateY =
        -1 * reelRef.current.getBoundingClientRect().top + slideRef.getBoundingClientRect().top;

      if (translateY !== carouselState.posY) {
        carouselDispatch({
          type: ACTIONS.SET_POS,
          payload: {
            posY: translateY,
            posX: 0,
          },
        });
      }
    }
  }, [
    carouselState.containerWidth,
    carouselState.slideIndex,
    carouselState.type,
    carouselState.posX,
    carouselState.posY,
  ]);

  // Update Carousel CSS styles on Position change
  useEffect(() => {
    let newReelStyles: React.CSSProperties = {};
    if (carouselState.type === CAROUSEL_TYPE.HORIZONTAL) {
      newReelStyles = {
        height: `${carouselState.slideHeight}`,
        transform: `translateX(-${carouselState.posX}px)`,
      };
    } else {
      newReelStyles = {
        width: `${carouselState.slideWidth}`,
        transform: `translateY(-${carouselState.posY}px)`,
      };
    }

    if (reelStyles.transform === newReelStyles.transform) {
      return;
    }

    setReelStyles(newReelStyles);
  }, [
    carouselState.type,
    carouselState.slideWidth,
    carouselState.slideHeight,
    carouselState.posX,
    carouselState.posY,
    reelStyles,
    setReelStyles,
  ]);

  useEffect(() => {
    let timer: number;
    const timerFunc = () => {
      let newIndex = carouselState.realSlideIndex + 1;
      if (newIndex === carouselState.numberOfSlides) {
        newIndex = 0;
      }
      carouselDispatch({
        type: ACTIONS.SET_SLIDE,
        payload: { slideIndex: newIndex },
      });
    };

    if (autoPlay) {
      timer = window.setTimeout(timerFunc, autoPlayDuration);
    }

    return () => window.clearInterval(timer);
  }, [carouselState.realSlideIndex, carouselState.numberOfSlides, autoPlay, autoPlayDuration]);

  const isLastSlide = carouselState.realSlideIndex + carouselState.slidesInFrame >= children.length;
  const isFirstSlide = carouselState.realSlideIndex === 0;

  const trackFirstImageSwiped = useCallback(() => {
    setIsCarouselFirstScroll(false);
    if (sendFirstImageSwipeTracking) sendFirstImageSwipeTracking();
  }, [sendFirstImageSwipeTracking]);

  const moveNext = useCallback(() => {
    if (loopOverSlides && isLastSlide) {
      carouselDispatch({ type: ACTIONS.SET_SLIDE, payload: { slideIndex: 0 } });
    } else if (isLastSlide === false) {
      carouselDispatch({ type: ACTIONS.SLIDE_NEXT });
    }
    if (isCarouselFirstScroll) trackFirstImageSwiped();
    updateImagesSwipedCount();
  }, [
    isCarouselFirstScroll,
    isLastSlide,
    loopOverSlides,
    trackFirstImageSwiped,
    updateImagesSwipedCount,
  ]);

  const movePrev = useCallback(() => {
    if (loopOverSlides && isFirstSlide) {
      carouselDispatch({
        type: ACTIONS.SET_SLIDE,
        payload: { slideIndex: children.length - 1 },
      });
    } else if (isFirstSlide === false) {
      carouselDispatch({ type: ACTIONS.SLIDE_PREV });
    }
    if (isCarouselFirstScroll) trackFirstImageSwiped();
  }, [loopOverSlides, isFirstSlide, isCarouselFirstScroll, trackFirstImageSwiped, children.length]);

  const isHorizontal = carouselState.type === CAROUSEL_TYPE.HORIZONTAL;

  const isNextDisabled = isLastSlide && loopOverSlides === false;
  const isPrevDisabled = isFirstSlide && loopOverSlides === false;

  const nextButtonComponent = useMemo(
    () =>
      nextButton || (
        <NavButton
          type={NAV_BUTTON.NEXT}
          oritentation={type}
          isDisabled={isNextDisabled}
          overflowChevron={true}
        />
      ),
    [isNextDisabled, nextButton, type]
  );

  const prevButtonComponent = useMemo(
    () =>
      prevButton || (
        <NavButton
          type={NAV_BUTTON.PREV}
          oritentation={type}
          isDisabled={isPrevDisabled}
          overflowChevron={true}
        />
      ),
    [isPrevDisabled, prevButton, type]
  );

  const getSlideStyles = (isLast: boolean): React.CSSProperties => {
    const marginStyles: React.CSSProperties = {};
    const marginKey = isHorizontal ? 'marginRight' : 'marginBottom';

    if (isLast === false) {
      const margin = ignoreRightMargin ? customRightMargin : carouselState.slideMargin;
      marginStyles[marginKey] = margin;
    }

    return {
      width: `${carouselState.slideWidth}px`,
      height: `${carouselState.slideHeight}px`,
      ...marginStyles,
      ...overrideSlideStyles,
    };
  };

  const containerStyle = useMemo(() => {
    if (showNav === false) {
      return {};
    }

    if (isBottomNav) {
      return {
        paddingBottom: `${navPadding}px`,
      };
    }

    return isHorizontal ? { padding: `0 ${navPadding}px` } : { padding: `${navPadding}px 0` };
  }, [showNav, isBottomNav, isHorizontal, navPadding]);

  const navStyle = useMemo(() => {
    if (showNav === false) {
      return {};
    }

    return isHorizontal
      ? { ...navCustomStyles, width: `${navPadding}px` }
      : { ...navCustomStyles, height: `${navPadding}px` };
  }, [isHorizontal, showNav, navCustomStyles, navPadding]);

  const swipeableProps = useMemo(
    () =>
      isHorizontal
        ? {
            onSwipedLeft: moveNext,
            onSwipedRight: movePrev,
          }
        : {
            onSwipedUp: moveNext,
            onSwipedDown: movePrev,
          },
    [isHorizontal, moveNext, movePrev]
  );

  return (
    <Container
      ref={containerRef}
      style={containerStyle}
      className={className}
      data-test="mobile-carosuel"
    >
      <Swipeable {...swipeableProps} preventDefaultTouchmoveEvent={true}>
        <Reel
          isVertical={carouselState.type === CAROUSEL_TYPE.VERTICAL}
          style={reelStyles}
          ref={reelRef}
        >
          {children.map((child, idx) => (
            <div
              key={'mobile-carousel-slide-' + idx}
              tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
              style={getSlideStyles(idx === children.length - 1)}
              ref={(ref: HTMLDivElement) => (slidesRef.current[idx] = ref)}
              className={slideClassName}
            >
              {child}
            </div>
          ))}
        </Reel>
      </Swipeable>
      {carouselState.canShowNav && (
        <>
          <Button
            aria-label="Previous"
            style={navStyle}
            onClick={movePrev}
            disabled={isPrevDisabled}
            isHorizontal={isHorizontal}
          >
            {prevButtonComponent}
          </Button>
          <Button
            aria-label="Next"
            style={navStyle}
            onClick={moveNext}
            disabled={isNextDisabled}
            isHorizontal={isHorizontal}
            isBottomNav={isBottomNav}
            isNext
          >
            {nextButtonComponent}
          </Button>
        </>
      )}
    </Container>
  );
}

export default memo(Carousel);
